
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from "@/components/ui/ThemeProvider"
import { Toaster } from "@/components/ui/toaster"
import { SidebarProvider } from "@/components/ui/sidebar"

import Layout from '@/components/layout/Layout';
import Dashboard from '@/pages/Dashboard';
import Collection from '@/pages/Collection';
import TagPage from '@/pages/TagPage';
import Board from '@/pages/Board';
import Archive from '@/pages/Archive';
import InBox from '@/pages/InBox';
import FocusedContent from '@/pages/FocusedContent';
import Auth from '@/pages/Auth';
import Index from '@/pages/Index';
import Releases from '@/pages/Releases';
import Workflow from '@/pages/Workflow';
import Roadmap from '@/pages/Roadmap';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import AdminAnalytics from '@/pages/AdminAnalytics';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AuthRouteGuard, PublicRouteGuard } from '@/components/auth/RouteGuard';
import { CollectionsProvider } from '@/contexts/collections';
import { BoardsProvider } from '@/contexts/boards';
import { TagsProvider } from '@/components/tags/TagsProvider';
import { SelectionProvider } from '@/contexts/selection/SelectionContext';
import { SearchProvider } from '@/contexts/search/SearchContext';
import { AnalyticsProvider } from "@/contexts/AnalyticsContext";

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
          <CollectionsProvider>
            <BoardsProvider>
              <TagsProvider>
                <SelectionProvider>
                  <SearchProvider>
                    <Toaster />
                    <BrowserRouter>
                      <AnalyticsProvider>
                        <Routes>
                          <Route path="/" element={
                            <PublicRouteGuard>
                              <Index />
                            </PublicRouteGuard>
                          } />
                          <Route path="/auth" element={
                            <PublicRouteGuard>
                              <Auth />
                            </PublicRouteGuard>
                          } />
                          <Route path="/releases" element={<Releases />} />
                          <Route path="/intro" element={<Workflow />} />
                          <Route path="/workflow" element={<Workflow />} />
                          <Route path="/roadmap" element={<Roadmap />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/terms" element={<PrivacyPolicy />} />
                          <Route path="/cookies" element={<PrivacyPolicy />} />
                          <Route path="/*" element={
                            <AuthRouteGuard>
                              <SidebarProvider>
                                <Routes>
                                  <Route path="/" element={<Layout />}>
                                    <Route index element={<Dashboard />} />
                                    <Route path="dashboard" element={<Dashboard />} />
                                    <Route path="collection/:id" element={<Collection />} />
                                    <Route path="tag/:id" element={<TagPage />} />
                                    <Route path="board/:id" element={<Board />} />
                                    <Route path="archive" element={<Archive />} />
                                    <Route path="inbox" element={<InBox />} />
                                    <Route path="content/:id" element={<FocusedContent />} />
                                    <Route path="admin/analytics" element={<AdminAnalytics />} />
                                    <Route path="admin/thumbnail-migration" element={<AdminAnalytics />} />
                                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                                  </Route>
                                </Routes>
                              </SidebarProvider>
                            </AuthRouteGuard>
                          } />
                        </Routes>
                      </AnalyticsProvider>
                    </BrowserRouter>
                  </SearchProvider>
                </SelectionProvider>
              </TagsProvider>
            </BoardsProvider>
          </CollectionsProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
